// src/components/Social/SocialMeetupsPanel.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import './SocialMeetupsPanel.css';

import 'leaflet/dist/leaflet.css';
import API_BASE_URL from '../../config/api';
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  useMap,
} from 'react-leaflet';

const API_URL = API_BASE_URL;

const buildOsmEmbedUrl = (lat, lon) => {
  const d = 0.01;
  const minLon = lon - d;
  const minLat = lat - d;
  const maxLon = lon + d;
  const maxLat = lat + d;

  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
    bbox
  )}&layer=mapnik&marker=${lat},${lon}`;
};

const buildOsmPageUrl = (lat, lon) =>
  `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hour = pad(d.getHours());
  const minute = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

function normalizeLatLonPoints(points) {
  if (!Array.isArray(points)) return [];
  const out = [];

  for (const p of points) {
    if (!Array.isArray(p) || p.length < 2) continue;
    let a = Number(p[0]);
    let b = Number(p[1]);
    if (Number.isNaN(a) || Number.isNaN(b)) continue;

    let lat = a;
    let lon = b;

    if (Math.abs(lat) > 90 && Math.abs(lon) <= 90) {
      lat = b;
      lon = a;
    }
    if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
      out.push([lat, lon]);
    }
  }

  return out;
}

function computeBounds(latLonPoints) {
  if (!latLonPoints.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;

  for (const [lat, lon] of latLonPoints) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  }

  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
}

const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!bounds) return;
    try {
      map.fitBounds(bounds, { padding: [16, 16] });
    } catch (_) {
      // noop
    }
  }, [bounds, map]);

  return null;
};

const RouteMap = ({ points, height = 320 }) => {
  const latLon = useMemo(() => normalizeLatLonPoints(points), [points]);
  const bounds = useMemo(() => computeBounds(latLon), [latLon]);

  if (!latLon.length) return null;

  const start = latLon[0];
  const end = latLon[latLon.length - 1];

  return (
    <div style={{ width: '100%', height }}>
      <MapContainer
        style={{ width: '100%', height: '100%' }}
        center={start}
        zoom={13}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Polyline positions={latLon} />
        <CircleMarker center={start} radius={6} pathOptions={{}} />
        <CircleMarker center={end} radius={6} pathOptions={{}} />
        <FitBounds bounds={bounds} />
      </MapContainer>
    </div>
  );
};

const SocialMeetupsPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navState = location?.state || {};
  const stateGroupId = navState?.groupId ? String(navState.groupId) : '';
  const stateEventId = navState?.eventId ? String(navState.eventId) : '';

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [title, setTitle] = useState('');
  const [sport, setSport] = useState('running');
  const [description, setDescription] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [meetingCity, setMeetingCity] = useState('');
  const [meetingCommune, setMeetingCommune] = useState('');
  const [meetingCountry, setMeetingCountry] = useState('Chile');
  const [routeUrl, setRouteUrl] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [showDetailsToRegisteredOnly, setShowDetailsToRegisteredOnly] = useState(true);

  const [routeGpxFile, setRouteGpxFile] = useState(null);
  const [routeGpxMeta, setRouteGpxMeta] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  const [emailsRaw, setEmailsRaw] = useState('');
  const [createdEventId, setCreatedEventId] = useState(null);
  const [lastCreatedEvent, setLastCreatedEvent] = useState(null);

  const groupIdFromUrl = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const gid = (sp.get('groupId') || '').trim();
    return gid || '';
  }, [location.search]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || localStorage.getItem('userID');

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (userId) headers['x-user-id'] = userId;
    return headers;
  }, []);

  const authHeadersNoJson = useCallback(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || localStorage.getItem('userID');

    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (userId) headers['x-user-id'] = userId;
    return headers;
  }, []);

  const loadEventIntoForm = (ev) => {
    if (!ev) return;

    setTitle(ev.title || '');
    setSport(ev.sport || 'running');
    setDescription(ev.description || '');
    setDateTime(formatDateTimeLocal(ev.dateTime));
    setMeetingAddress(ev.meetingAddress || '');
    setMeetingCity(ev.meetingCity || '');
    setMeetingCommune(ev.meetingCommune || '');
    setMeetingCountry(ev.meetingCountry || 'Chile');
    setRouteUrl(ev.routeUrl || '');
    setEmergencyPhone(ev.emergencyPhone || '');
    setMaxParticipants(typeof ev.maxParticipants === 'number' ? String(ev.maxParticipants) : '');
    setShowDetailsToRegisteredOnly(!!ev.showDetailsToRegisteredOnly);

    setEditingEventId(ev._id);
    setCreatedEventId(ev._id);
    setLastCreatedEvent(ev);

    setRouteGpxMeta(ev.routeGpx || null);
    setRouteGpxFile(null);
  };

  const fetchMyGroups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/social/groups/mine`, {
        headers: authHeaders(),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || t('social.meetupsPanel.errors.loadGroups'));
      }

      const arr = Array.isArray(data) ? data : [];
      setGroups(arr);

      const fromLs = (localStorage.getItem('socialSelectedGroupId') || '').trim();

      const candidate =
        (stateGroupId || '').trim() ||
        groupIdFromUrl ||
        fromLs ||
        (arr.length ? String(arr[0]._id) : '');

      if (candidate) {
        setSelectedGroupId(candidate);

        const gObj = arr.find((g) => String(g._id) === String(candidate));
        setSelectedGroupName(gObj?.name || '');

        localStorage.setItem('socialSelectedGroupId', candidate);
      } else {
        setSelectedGroupId('');
        setSelectedGroupName('');
      }
    } catch (err) {
      console.error(err);
      setMessage(err.message || t('social.meetupsPanel.errors.loadGroups'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, groupIdFromUrl, stateGroupId, t]);

  useEffect(() => {
    fetchMyGroups();
  }, [fetchMyGroups]);

  useEffect(() => {
    const loadEvents = async () => {
      setEvents([]);
      setEditingEventId(null);
      setCreatedEventId(null);
      setLastCreatedEvent(null);

      setTitle('');
      setSport('running');
      setDescription('');
      setDateTime('');
      setMeetingAddress('');
      setMeetingCity('');
      setMeetingCommune('');
      setMeetingCountry('Chile');
      setRouteUrl('');
      setEmergencyPhone('');
      setMaxParticipants('');
      setShowDetailsToRegisteredOnly(true);

      setRouteGpxFile(null);
      setRouteGpxMeta(null);

      if (!selectedGroupId) return;

      try {
        setEventsLoading(true);
        const res = await fetch(
          `${API_URL}/api/social/events/by-group/${selectedGroupId}`,
          { headers: authHeaders() }
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || t('social.meetupsPanel.errors.loadEvents'));
        }

        const arr = Array.isArray(data) ? data : [];
        setEvents(arr);

        if (arr.length > 0) {
          const pick = stateEventId
            ? arr.find((ev) => String(ev._id) === String(stateEventId)) || arr[0]
            : arr[0];

          loadEventIntoForm(pick);
        }
      } catch (err) {
        console.error('[SocialMeetupsPanel] error fetchEvents', err);
      } finally {
        setEventsLoading(false);
      }
    };

    loadEvents();
  }, [selectedGroupId, authHeaders, stateEventId, t]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!selectedGroupId) {
      setMessage(t('social.meetupsPanel.messages.noActiveGroup'));
      return;
    }

    const isEditing = !!editingEventId;

    try {
      setLoading(true);

      const url = isEditing
        ? `${API_URL}/api/social/events/${editingEventId}`
        : `${API_URL}/api/social/events`;

      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({
          groupId: selectedGroupId,
          title,
          sport,
          description,
          dateTime,
          meetingAddress,
          meetingCity,
          meetingCommune,
          meetingCountry,
          routeUrl,
          emergencyPhone,
          showDetailsToRegisteredOnly,
          maxParticipants: maxParticipants ? Number(maxParticipants) : 0,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
            (isEditing
              ? t('social.meetupsPanel.errors.updateEvent')
              : t('social.meetupsPanel.errors.createEvent'))
        );
      }

      if (isEditing) {
        setEvents((prev) => prev.map((ev) => (ev._id === data._id ? data : ev)));
        setMessage(t('social.meetupsPanel.messages.eventUpdated'));
      } else {
        setEvents((prev) => [data, ...prev]);
        setMessage(t('social.meetupsPanel.messages.eventCreated'));
      }

      setCreatedEventId(data._id);
      setLastCreatedEvent(data);
      setEditingEventId(data._id);

      if (routeGpxFile) {
        const fd = new FormData();
        fd.append('routeGpx', routeGpxFile);

        const up = await fetch(`${API_URL}/api/social/events/${data._id}/route-gpx`, {
          method: 'POST',
          headers: authHeadersNoJson(),
          body: fd,
        });

        const upData = await up.json().catch(() => ({}));

        if (!up.ok) {
          throw new Error(upData.message || t('social.meetupsPanel.errors.uploadGpx'));
        }

        setEvents((prev) => prev.map((ev) => (ev._id === upData._id ? upData : ev)));
        setLastCreatedEvent(upData);
        setRouteGpxMeta(upData.routeGpx || null);
        setRouteGpxFile(null);

        setMessage((prev) =>
          prev
            ? `${prev} ${t('social.meetupsPanel.messages.gpxUploaded')}`
            : t('social.meetupsPanel.messages.gpxUploaded')
        );
      } else {
        setRouteGpxMeta(data.routeGpx || null);
      }
    } catch (err) {
      console.error(err);
      setMessage(err.message || t('social.meetupsPanel.errors.saveEvent'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditEventClick = (eventId) => {
    const ev = events.find((e) => e._id === eventId || e.id === eventId) || null;
    if (!ev) return;
    loadEventIntoForm(ev);
    setMessage(t('social.meetupsPanel.messages.editingExisting'));
  };

  const handleNewEventClick = () => {
    setEditingEventId(null);
    setCreatedEventId(null);
    setLastCreatedEvent(null);

    setTitle('');
    setSport('running');
    setDescription('');
    setDateTime('');
    setMeetingAddress('');
    setMeetingCity('');
    setMeetingCommune('');
    setMeetingCountry('Chile');
    setRouteUrl('');
    setEmergencyPhone('');
    setMaxParticipants('');
    setShowDetailsToRegisteredOnly(true);

    setRouteGpxFile(null);
    setRouteGpxMeta(null);

    setMessage(t('social.meetupsPanel.messages.creatingNew'));
  };

  const handleSendInvites = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!createdEventId) {
      setMessage(t('social.meetupsPanel.messages.saveBeforeInvites'));
      return;
    }

    const emails = emailsRaw
      .split(/[\n,;]+/)
      .map((v) => v.trim())
      .filter(Boolean);

    if (!emails.length) {
      setMessage(t('social.meetupsPanel.messages.enterAtLeastOneEmail'));
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/social/events/${createdEventId}/invite`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ emails }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || t('social.meetupsPanel.errors.sendInvites'));
      }

      setMessage(t('social.meetupsPanel.messages.invitesSent'));
      setEmailsRaw('');
    } catch (err) {
      console.error(err);
      setMessage(err.message || t('social.meetupsPanel.errors.sendInvites'));
    } finally {
      setLoading(false);
    }
  };

  let mapLat = null;
  let mapLon = null;
  let hasValidLocation = false;

  if (
    lastCreatedEvent &&
    lastCreatedEvent.location &&
    Array.isArray(lastCreatedEvent.location.coordinates) &&
    lastCreatedEvent.location.coordinates.length === 2
  ) {
    let [c0, c1] = lastCreatedEvent.location.coordinates;

    let lon = Number(c0);
    let lat = Number(c1);

    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) {
      const tmp = lon;
      lon = lat;
      lat = tmp;
    }

    if (!Number.isNaN(lat) && !Number.isNaN(lon) && !(lat === 0 && lon === 0)) {
      mapLat = lat;
      mapLon = lon;
      hasValidLocation = true;
    }
  }

  const routePoints = lastCreatedEvent?.routeGpx?.points || [];

  const groupHint =
    selectedGroupName ||
    groups.find((g) => String(g._id) === String(selectedGroupId))?.name ||
    '';

  return (
    <div className="social-meetups-root">
      <div className="social-meetups-inner">
        <header className="social-meetups-header">
          <div>
            <h1>{t('social.meetupsPanel.title')}</h1>
            <p>
              {t('social.meetupsPanel.subtitleLine1')}
              <br />
              <b>{t('social.meetupsPanel.subtitleLine2')}</b>
              {groupHint ? (
                <>
                  <br />
                  {t('social.meetupsPanel.activeGroup')} <b>{groupHint}</b>
                </>
              ) : null}
            </p>
          </div>

          <button
            type="button"
            className="social-meetups-btn"
            onClick={() => navigate('/social/meetups/board')}
          >
            {t('social.meetupsPanel.backToBoard')}
          </button>
        </header>

        {message && <div className="social-meetups-message">{message}</div>}

        <div className="social-meetups-grid">
          <section className="social-meetups-section">
            <div className="social-meetups-section-titlebar">
              <h2>{t('social.meetupsPanel.form.title')}</h2>

              {editingEventId && (
                <button
                  type="button"
                  className="social-meetups-btn"
                  onClick={handleNewEventClick}
                >
                  {t('social.meetupsPanel.form.newEvent')}
                </button>
              )}
            </div>

            <form onSubmit={handleSaveEvent} className="social-meetups-form">
              <div className="social-meetups-field">
                <label>{t('social.meetupsPanel.form.fields.title')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('social.meetupsPanel.form.placeholders.title')}
                  required
                />
              </div>

              <div className="social-meetups-field">
                <label>
                  {t('social.meetupsPanel.form.fields.sport')}
                </label>
                <select value={sport} onChange={(e) => setSport(e.target.value)}>
                  <option value="running">
                    {t('social.meetupsPanel.form.sports.running')}
                  </option>
                  <option value="cycling">
                    {t('social.meetupsPanel.form.sports.cycling')}
                  </option>
                  <option value="swimming">
                    {t('social.meetupsPanel.form.sports.swimming')}
                  </option>
                  <option value="trailrunning">
                    {t('social.meetupsPanel.form.sports.trailrunning')}
                  </option>
                  <option value="strength">
                    {t('social.meetupsPanel.form.sports.strength')}
                  </option>
                  <option value="walking">
                    {t('social.meetupsPanel.form.sports.walking')}
                  </option>
                  <option value="other">
                    {t('social.meetupsPanel.form.sports.other')}
                  </option>
                </select>
              </div>

              <div className="social-meetups-field">
                <label>{t('social.meetupsPanel.form.fields.description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('social.meetupsPanel.form.placeholders.description')}
                />
              </div>

              <div className="social-meetups-field-inline">
                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.dateTime')}</label>
                  <input
                    type="datetime-local"
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    required
                  />
                </div>

                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.maxParticipants')}</label>
                  <input
                    type="number"
                    min="0"
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(e.target.value)}
                  />
                </div>
              </div>

              <div className="social-meetups-field">
                <label>{t('social.meetupsPanel.form.fields.meetingAddress')}</label>
                <input
                  type="text"
                  value={meetingAddress}
                  onChange={(e) => setMeetingAddress(e.target.value)}
                  placeholder={t('social.meetupsPanel.form.placeholders.meetingAddress')}
                  required
                />
              </div>

              <div className="social-meetups-field-inline">
                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.city')}</label>
                  <input
                    type="text"
                    value={meetingCity}
                    onChange={(e) => setMeetingCity(e.target.value)}
                    placeholder={t('social.meetupsPanel.form.placeholders.city')}
                  />
                </div>

                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.commune')}</label>
                  <input
                    type="text"
                    value={meetingCommune}
                    onChange={(e) => setMeetingCommune(e.target.value)}
                    placeholder={t('social.meetupsPanel.form.placeholders.commune')}
                  />
                </div>
              </div>

              <div className="social-meetups-field">
                <label>{t('social.meetupsPanel.form.fields.country')}</label>
                <input
                  type="text"
                  value={meetingCountry}
                  onChange={(e) => setMeetingCountry(e.target.value)}
                  placeholder={t('social.meetupsPanel.form.placeholders.country')}
                />
              </div>

              <div className="social-meetups-field-inline">
                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.emergencyPhone')}</label>
                  <input
                    type="tel"
                    value={emergencyPhone}
                    onChange={(e) => setEmergencyPhone(e.target.value)}
                    placeholder={t('social.meetupsPanel.form.placeholders.emergencyPhone')}
                  />
                </div>

                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.form.fields.routeUrl')}</label>
                  <input
                    type="url"
                    value={routeUrl}
                    onChange={(e) => setRouteUrl(e.target.value)}
                    placeholder={t('social.meetupsPanel.form.placeholders.routeUrl')}
                  />
                </div>
              </div>

              <div className="social-meetups-field">
                <label>{t('social.meetupsPanel.form.fields.gpxFile')}</label>
                <input
                  type="file"
                  accept=".gpx"
                  onChange={(e) => setRouteGpxFile(e.target.files?.[0] || null)}
                />

                {routeGpxMeta?.filePath && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    {t('social.meetupsPanel.form.currentGpx')}{' '}
                    <a
                      href={`${API_URL}${routeGpxMeta.filePath}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('social.meetupsPanel.form.download')}
                    </a>
                  </div>
                )}
              </div>

              <div className="social-meetups-field social-meetups-field-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={showDetailsToRegisteredOnly}
                    onChange={(e) => setShowDetailsToRegisteredOnly(e.target.checked)}
                  />
                  {t('social.meetupsPanel.form.fields.registeredOnly')}
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="social-cta-primary social-meetups-btn"
              >
                {editingEventId
                  ? t('social.meetupsPanel.form.saveChanges')
                  : t('social.meetupsPanel.form.create')}
              </button>
            </form>

            <div className="social-meetups-events-list">
              <label>{t('social.meetupsPanel.events.title')}</label>

              {!selectedGroupId && (
                <div className="social-meetups-placeholder">
                  {t('social.meetupsPanel.events.noActiveGroup')}
                </div>
              )}

              {selectedGroupId && eventsLoading && (
                <div className="social-meetups-placeholder">
                  {t('social.meetupsPanel.events.loading')}
                </div>
              )}

              {selectedGroupId && !eventsLoading && events.length === 0 && (
                <div className="social-meetups-placeholder">
                  {t('social.meetupsPanel.events.empty')}
                </div>
              )}

              {selectedGroupId && !eventsLoading && events.length > 0 && (
                <ul className="social-meetups-events-ul">
                  {events.map((ev) => (
                    <li key={ev._id} className="social-meetups-events-item">
                      <div className="social-meetups-events-main">
                        <div className="social-meetups-events-title">{ev.title}</div>
                        <div className="social-meetups-events-meta">
                          {ev.dateTime ? new Date(ev.dateTime).toLocaleString() : ''}
                          {(ev.meetingCity || ev.meetingCommune) && (
                            <>
                              {' · '}
                              {[ev.meetingCommune, ev.meetingCity].filter(Boolean).join(' / ')}
                            </>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="social-meetups-btn"
                          onClick={() => navigate(`/social/meetups/${ev._id}`)}
                          title="Abrir el detalle del encuentro y su chat grupal"
                        >
                          Ver / Chat
                        </button>

                        <button
                          type="button"
                          className="social-meetups-btn"
                          onClick={() => handleEditEventClick(ev._id)}
                        >
                          {t('social.meetupsPanel.events.edit')}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {hasValidLocation ? (
          <section className="social-meetups-section social-meetups-section--full">
            <h2>{t('social.meetupsPanel.location.title')}</h2>

            <p className="social-meetups-subtext">
              {lastCreatedEvent?.meetingAddress}
              {lastCreatedEvent &&
                (lastCreatedEvent.meetingCommune || lastCreatedEvent.meetingCity) && (
                  <>
                    {', '}
                    {[lastCreatedEvent.meetingCommune, lastCreatedEvent.meetingCity]
                      .filter(Boolean)
                      .join(' / ')}
                  </>
                )}
              {lastCreatedEvent?.meetingCountry ? ` / ${lastCreatedEvent.meetingCountry}` : ''}
            </p>

            <div className="social-meetups-map">
              <iframe
                title={t('social.meetupsPanel.location.mapTitle')}
                src={buildOsmEmbedUrl(mapLat, mapLon)}
                loading="lazy"
              />
            </div>

            <div className="social-meetups-map-debug">
              <small>
                Coords debug: lat {mapLat}, lon {mapLon} · provider{' '}
                {lastCreatedEvent?.location?.provider || 'n/a'}
              </small>
            </div>

            <div className="social-meetups-map-link">
              <a
                href={buildOsmPageUrl(mapLat, mapLon)}
                target="_blank"
                rel="noreferrer"
              >
                {t('social.meetupsPanel.location.viewOnMap')}
              </a>
            </div>

            {Array.isArray(routePoints) && routePoints.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ marginBottom: 8 }}>{t('social.meetupsPanel.route.title')}</h3>
                <RouteMap points={routePoints} height={340} />
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                  {t('social.meetupsPanel.route.points', { count: routePoints.length })}
                  {routeGpxMeta?.filePath ? (
                    <>
                      {' · '}
                      <a
                        href={`${API_URL}${routeGpxMeta.filePath}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('social.meetupsPanel.route.downloadGpx')}
                      </a>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </section>
        ) : (
          lastCreatedEvent && (
            <section className="social-meetups-section social-meetups-section--full">
              <h2>{t('social.meetupsPanel.location.previewTitle')}</h2>
              <p className="social-meetups-subtext">
                {t('social.meetupsPanel.location.previewError')}
              </p>
            </section>
          )
        )}

        <section className="social-meetups-section social-meetups-section--full">
          <h2>{t('social.meetupsPanel.invites.title')}</h2>

          <div className="social-meetups-invite-grid">
            <div className="social-meetups-invite-column">
              <h3>{t('social.meetupsPanel.invites.additionalEmails')}</h3>

              <form onSubmit={handleSendInvites} className="social-meetups-form">
                <div className="social-meetups-field">
                  <label>{t('social.meetupsPanel.invites.emailsLabel')}</label>
                  <textarea
                    value={emailsRaw}
                    onChange={(e) => setEmailsRaw(e.target.value)}
                    placeholder={t('social.meetupsPanel.invites.emailsPlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="social-cta-primary social-meetups-btn"
                >
                  {t('social.meetupsPanel.invites.send')}
                </button>
              </form>

              <div className="social-meetups-tips">
                <h3>{t('social.meetupsPanel.tips.title')}</h3>
                <ul>
                  <li>{t('social.meetupsPanel.tips.item1')}</li>
                  <li>{t('social.meetupsPanel.tips.item2')}</li>
                  <li>{t('social.meetupsPanel.tips.item3')}</li>
                  <li>{t('social.meetupsPanel.tips.item4')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SocialMeetupsPanel;